import React from 'react';

import { HOME } from '../../theme/tokens';
import {
  Root,
  Rows,
  Row,
  Name,
  Track,
  CompactRows,
  CompactRow,
  Fill,
  Marker,
  Vals,
  Cur,
  Glyph,
  SwatchBar,
  SwatchTick,
  Legend,
  LegItem,
  LegText,
  LiveDot,
  Tooltip,
  TipHead,
  TipRows,
  TipRow,
  TipName,
  TipValue,
} from './DayBars.styles';

/** One series in the day comparison: its prior-day and current-day totals (already
 *  in display scale). `dashed` mirrors the LineChart's dashed series (rendered as an
 *  outlined fill). */
export type DayBarSeries = {
  /** Series name — shown as the row label + tooltip heading, and the React key. */
  name: string;
  /** Bar fill — a `HOME_SERIES_COLORS` token, matching the LineChart line. */
  color: string;
  /** Prior day's FULL-day total (the reference tick). */
  prev: number;
  /** Current day's total (so-far when `view` is `today`). */
  current: number;
  /** Render an outlined (vs solid) fill, mirroring a dashed line series. */
  dashed?: boolean;
  /** Scale this row to its own day max and leave it out of the shared scale: a series of a much larger magnitude
   *  (website visits beside the sign-ups) that would otherwise shrink the others to nubs. */
  ownScale?: boolean;
};

export type DayBarsProps = {
  series: DayBarSeries[];
  /**
   * Which two-day comparison this is. `today` is LIVE: the legend shows the live
   * dot; `yesterday` compares two completed days.
   */
  view: 'today' | 'yesterday';
  /** Total height, mirroring the LineChart `height` it stands in for. */
  height?: number;
  /** Bars-only layout for the small channel mini-cards (no name column, value pairs,
   *  or legend). Hover still raises the tooltip. */
  compact?: boolean;
  /** Format a value for the readouts (e.g. compact money / counts). */
  formatValue?: (n: number) => string;
  /** Describes the comparison for assistive tech (e.g. "New players, today vs yesterday"). */
  ariaLabel?: string;
  className?: string;
};

// A little headroom past the longest value so a full bar never kisses the edge.
const HEADROOM = 1.08;

/** Flip to true one frame after mount so the fills grow from the axis instead of
 *  appearing at full length. */
const useGrown = (): boolean => {
  const [grown, setGrown] = React.useState(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return grown;
};

const defaultFormat = (n: number): string => `${Math.round(n)}`;

type Hover = { i: number; x: number; y: number; w: number };

/**
 * One bullet row per series (the "day-based" view that replaces the hourly LineChart
 * for the Today / Yesterday ranges, which have no usable hourly slices). Each row's
 * coloured fill is the current day; a thin tick on the same track marks the prior day
 * (the pace reference). On the LIVE `today` view the legend shows a pulsing dot.
 * Hovering a row raises a tooltip with both days' values, using the SAME bar/tick
 * swatches as the legend so "yesterday" reads identically.
 */
export const DayBars = ({
  series,
  view,
  height = 170,
  compact = false,
  formatValue = defaultFormat,
  ariaLabel,
  className,
}: DayBarsProps): React.ReactElement => {
  const grown = useGrown();
  const live = view === 'today';
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [hover, setHover] = React.useState<Hover | null>(null);

  // Sections share comparable units (e.g. GGR / Stakes / Wins), so ONE scale across
  // every series shows their relative magnitude. The compact channel cards mix units
  // (counts vs money), so each row scales to its OWN day max — otherwise the small
  // series collapse to nubs beside the large one. Either way `prev`/`current` of a
  // row share a scale, so the fill vs the tick is an honest today-vs-yesterday read.
  // An `ownScale` row always scales to itself, and stays out of the shared scale.
  const globalMax = Math.max(1, ...series.filter((s) => !s.ownScale).flatMap((s) => [s.prev, s.current])) * HEADROOM;
  const pctOf = (s: DayBarSeries, v: number): number => {
    const m = compact || s.ownScale ? Math.max(1, s.prev, s.current) * HEADROOM : globalMax;
    return v <= 0 ? 0 : Math.max(2, (v / m) * 100);
  };

  const track = (i: number) => (e: React.PointerEvent<HTMLDivElement>): void => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({ i, x: e.clientX - rect.left, y: e.clientY - rect.top, w: rect.width });
  };
  const clear = (): void => setHover(null);

  const active = hover ? series[hover.i] : null;

  const tooltip = active && hover && (
    <Tooltip data-testid="day-tooltip" $on $flip={hover.x > hover.w * 0.6} style={{ left: hover.x, top: hover.y }}>
      <TipHead text={active.name} />
      <TipRows>
        <TipRow>
          <Glyph>
            <SwatchBar $color={active.color} />
          </Glyph>
          {live ? <TipName text="Today" /> : <TipName text="Yesterday" />}
          <TipValue>{formatValue(active.current)}</TipValue>
        </TipRow>
        <TipRow>
          <Glyph>
            <SwatchTick />
          </Glyph>
          {live ? <TipName text="Yesterday" /> : <TipName text="Day before" />}
          <TipValue>{formatValue(active.prev)}</TipValue>
        </TipRow>
      </TipRows>
    </Tooltip>
  );

  if (compact) {
    return (
      <Root ref={rootRef} $h={height} className={className} role="group" aria-label={ariaLabel} onPointerLeave={clear}>
        <CompactRows>
          {series.map((s, i) => (
            <CompactRow key={s.name} onPointerMove={track(i)} onPointerDown={track(i)} onPointerLeave={clear}>
              <Track $compact>
                <Fill data-testid="day-bar" $pct={pctOf(s, s.current)} $color={s.color} $grown={grown} $outline={Boolean(s.dashed)} />
                <Marker data-testid="day-marker" $pct={pctOf(s, s.prev)} $grown={grown} />
              </Track>
            </CompactRow>
          ))}
        </CompactRows>
        {tooltip}
      </Root>
    );
  }

  return (
    <Root ref={rootRef} $h={height} className={className} role="group" aria-label={ariaLabel} onPointerLeave={clear}>
      <Rows>
        {series.map((s, i) => (
          <Row key={s.name} onPointerMove={track(i)} onPointerDown={track(i)} onPointerLeave={clear}>
            <Name text={s.name} />
            <Track>
              <Fill data-testid="day-bar" $pct={pctOf(s, s.current)} $color={s.color} $grown={grown} $outline={Boolean(s.dashed)} />
              <Marker data-testid="day-marker" $pct={pctOf(s, s.prev)} $grown={grown} />
            </Track>
            <Vals>
              <Cur>{formatValue(s.current)}</Cur> · {formatValue(s.prev)}
            </Vals>
          </Row>
        ))}
      </Rows>

      <Legend>
        <LegItem>
          {/* Neutral swatch — the legend is a generic key, not tied to one series. */}
          <Glyph>
            <SwatchBar $color={HOME.t400} />
          </Glyph>
          {/* Literal labels so they extract for i18n (live vs completed comparison). */}
          {live ? <LegText text="Today" /> : <LegText text="Yesterday" />}
          {live && <LiveDot data-testid="day-live-dot" aria-hidden="true" />}
        </LegItem>
        <LegItem>
          <Glyph>
            <SwatchTick />
          </Glyph>
          {live ? <LegText text="Yesterday" /> : <LegText text="Day before" />}
        </LegItem>
      </Legend>

      {tooltip}
    </Root>
  );
};

export default DayBars;
